import LanguageChanger from "@/components/LanguageChanger/languageChanger.component";
import ForgotPasswordDialog from "@/components/Login/forgotPasswordDialog.component";
import { ROUTE_HOME } from "@/constants/routes.constants";
import { sendLogin } from "@/models/socket/authenticate.model";
import { Button } from "@/shadcn/components/ui/button";
import { Input } from "@/shadcn/components/ui/input";
import { Label } from "@/shadcn/components/ui/label";
import { setLSAuthToken, setLSRefreshToken } from "@/utils/tokens.util";
import { isLanguageSupportEnabled } from "@/utils/utils.util";
import { validateEmail } from "@/utils/validation.util";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

type LoginProps = {
  email: string;
  setEmail: (email: string) => void;
  goToMagicLink: () => void;
};

const Login: React.FC<LoginProps> = ({ email, setEmail, goToMagicLink }) => {
  const { t } = useTranslation();
  const [password, setPassword] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [loggingIn, setLoggingIn] = useState<boolean>(false);
  const [loginCount, setLoginCount] = useState(0);
  const [loginCoolDown, setLoginCoolDown] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const languageSupportEnabled = isLanguageSupportEnabled(); // Check if language support is enabled

  // ------------------------------------------------------------
  // UseEffects handle cooldown to prevent server spamming
  // This logic is added to slow down repeated login attempts for security reasons

  useEffect(() => {
    if (loginCount === 3) {
      // After 3 attempts, apply a short cooldown to slow down brute force
      setCountdown(10);
      setLoginCoolDown(true);
    } else if (loginCount > 3) {
      // After more than 3, apply a longer cooldown to further discourage abuse
      setCountdown(60);
      setLoginCoolDown(true);
    } else {
      setLoginCoolDown(false);
    }
  }, [loginCount]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0) {
      // Decrement countdown every second to inform user when they can try again
      timer = setInterval(() => {
        setCountdown((c) => c - 1);
      }, 1000);
    } else {
      setLoginCoolDown(false);
    }
    // Clean up timer to avoid memory leaks
    return () => clearInterval(timer);
  }, [countdown]);
  // ------------------------------------------------------------

  function validateFormLogin(): { email: string; password: string } | false {
    const strippedEmail = email.replace(/\s/g, "");
    const strippedPassword = password.replace(/\s/g, "");
    if (!strippedEmail || strippedPassword === "") {
      setError(t("Please fill in all fields"));
      return false;
    }

    if (!validateEmail(strippedEmail)) {
      setError(t("Please enter a valid email"));
      return false;
    }

    return { email: strippedEmail, password: strippedPassword };
  }

  async function handleLogin() {
    setError("");
    const validatedForm = validateFormLogin();
    if (!validatedForm) return;

    setLoggingIn(true);

    // Increment login attempt count for cooldown logic
    setLoginCount((prevCount) => prevCount + 1);

    const response = await sendLogin(
      validatedForm.email,
      validatedForm.password
    );
    console.log(response);
    if (response.rStatus === "error") {
      setError(response.rData);
      setLoggingIn(false);
    } else {
      // On successful login, reset cooldown and login count
      setLSAuthToken(response.rData.authToken);
      setLSRefreshToken(response.rData.refreshToken);
      setCountdown(0);
      setLoginCount(0);
      setLoginCoolDown(false);
      window.location.href = ROUTE_HOME;
    }
  }

  return (
    <>
      <div className="grid w-full gap-2">
        <div className="grid grid-cols-2 items-center">
          <Label htmlFor="email" className="m-auto text-base font-semibold">
            {t("Email")}
          </Label>
          <Input
            type="text"
            className="w-full p-1 ps-2"
            id="email"
            placeholder={t("Enter email")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoCapitalize="off"
          />
        </div>
        <div className="grid grid-cols-2 items-center">
          <Label htmlFor="password" className="m-auto text-base font-semibold">
            {t("Password")}
          </Label>
          <Input
            type="password"
            className="w-full p-1 ps-2"
            id="password"
            placeholder={t("Enter password")}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            // on enter key press login
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleLogin();
              }
            }}
          />
        </div>
        {languageSupportEnabled && (
          <div className="grid grid-cols-2 items-center">
            <Label
              htmlFor="language-changer"
              className="m-auto text-base font-semibold"
            >
              {t("Language")}
            </Label>
            <LanguageChanger />
          </div>
        )}
      </div>
      {
        // Display error message if there is any
        error && <div className="mt-5 text-balance text-red-500">{error}</div>
      }
      <Button
        onClick={handleLogin}
        className="mt-5"
        // Disable button if cooldown is active to enforce waiting period
        disabled={email === "" || password === "" || loggingIn || loginCoolDown}
      >
        {loggingIn
          ? t("Logging in...")
          : loginCoolDown
            ? `${t("Submit Cooldown: ")} ${countdown}s`
            : t("Login")}
      </Button>
      <div className="mt-1 -mb-3">
        <ForgotPasswordDialog goToMagicLink={goToMagicLink} />
      </div>
    </>
  );
};

export default Login;
